import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import FilterIcon from '@/assets/icons/filters.svg';
import { FilterSidebarItem } from '@/features/canvas/components/FilterSidebarItem';
import MultiFilterToggle from '@/features/canvas/components/MultiFilterToggle/MultiFilterToggle';
import { ViewToggle } from '@/features/canvas/components/ViewToggle';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';

const EditorFilterSidebar = () => {
  const { t } = useTranslation();
  const { sortableFilters, reorderedFilterOptions } = useEditorContext();

  // Defensive sorting - handle both sortOrder and sort_order
  // Using type assertion to access properties that may exist
  type FilterWithBothSorts = {
    [key: string]: unknown;
    sortOrder?: number;
    sort_order?: number;
  };

  const sortedFilters = sortableFilters
    ? [...sortableFilters].sort((a, b) => {
        const aFilter = a as FilterWithBothSorts;
        const bFilter = b as FilterWithBothSorts;
        const aSort = aFilter.sortOrder ?? aFilter.sort_order ?? 0;
        const bSort = bFilter.sortOrder ?? bFilter.sort_order ?? 0;
        return aSort - bSort;
      })
    : [];

  const visibleFilters = sortedFilters.filter(filter => filter.isVisible);

  return (
    <Sidebar>
      <SidebarHeader>
        <ViewToggle />
      </SidebarHeader>
      <SidebarBody>
        {!!visibleFilters?.length && (
          <>
            <MultiFilterToggle />
            {visibleFilters.map(
              filter =>
                !!filter.options.length && (
                  <FilterSidebarItem
                    key={filter.id}
                    filter={filter}
                    optionsOverride={reorderedFilterOptions?.[filter.id!]}
                  />
                )
            )}
          </>
        )}
        {!visibleFilters?.length && (
          <EmptyFiltersWrapper>
            <FilterImageWrapper>
              <StyledImage src={FilterIcon} />
            </FilterImageWrapper>
            <EmptyFiltersText>{t('editor.filter_sidebar.add_filters')}</EmptyFiltersText>
          </EmptyFiltersWrapper>
        )}
      </SidebarBody>
    </Sidebar>
  );
};

const Sidebar = styled.div`
  background-color: ${({ theme }) => theme.palette.brand.white};
  width: 250px;
  flex: 1 0 250px;
  min-height: 0;
  padding: 2rem 0;
  border-radius: 1rem;
  overflow-y: auto;
  overflow-x: auto;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  width: 100%;
  padding: 0 2.5rem;
`;

const SidebarBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2.5rem;
  padding: 0 1rem;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
`;

const FilterImageWrapper = styled.div`
  width: 1.5rem;
  height: 1.5rem;
`;

const EmptyFiltersText = styled.div`
  font-size: 1.5rem;
  text-align: center;
  max-width: 125px;
`;

const EmptyFiltersWrapper = styled.div`
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

export { EditorFilterSidebar };
